import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useCartStore from '../store/cartStore';
import { formatEUR } from '../utils/format';

const SeasonalLandingPage = () => {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const addToCart = useCartStore(state => state.addItem);

  useEffect(() => {
    loadPage();
  }, [pageId]);

  const loadPage = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/seasonal-landing-pages/${pageId}`
      );

      if (response.ok) {
        const data = await response.json();
        setPage(data);
      } else {
        toast.error('Seite nicht gefunden');
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading page:', error);
      toast.error('Fehler beim Laden der Seite');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    if (!addToCart(product, 1)) return;
    toast.success(`${product.name} zum Warenkorb hinzugefügt!`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-center">
            <div className="h-8 w-64 bg-gray-200 rounded mb-4 mx-auto"></div>
            <div className="h-4 w-48 bg-gray-200 rounded mx-auto"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!page) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        {/* Hero Section with Image */}
        <div className="relative h-96 overflow-hidden">
          {page.image_url && (
            <img
              src={page.image_url}
              alt={page.title}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
            <div className="container mx-auto px-4 pb-12">
              <Button
                variant="ghost"
                className="text-white mb-4"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Zurück zur Startseite
              </Button>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                {page.title}
              </h1>
              {page.description && (
                <p className="text-xl text-white/90">{page.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Custom Text Section */}
        {page.custom_text && (
          <div className="py-12 bg-gray-50">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center">
                <p className="text-lg text-gray-700 whitespace-pre-line">
                  {page.custom_text}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Products Section */}
        {page.products && page.products.length > 0 && (
          <div className="py-12">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl font-bold mb-8 text-center">
                Unsere Auswahl für Sie
              </h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {page.products.map((product) => (
                  <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div
                      className="aspect-square overflow-hidden bg-gray-100 cursor-pointer"
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      {product.images?.[0] && (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3
                        className="font-semibold mb-2 cursor-pointer hover:text-blue-600 line-clamp-2"
                        onClick={() => navigate(`/products/${product.id}`)}
                      >
                        {product.name}
                      </h3>
                      {product.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {product.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold text-[#D4AF37]">
                          {formatEUR(product.price)}
                        </span>
                        <Button
                          size="sm"
                          onClick={() => handleAddToCart(product)}
                          className="bg-[#D4AF37] hover:bg-[#C9A961]"
                        >
                          <ShoppingCart className="h-4 w-4 mr-1" />
                          In den Warenkorb
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="py-12 bg-[#D4AF37] text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Entdecken Sie mehr in unserem Shop
            </h2>
            <Button
              onClick={() => navigate('/products')}
              variant="secondary"
              size="lg"
              className="bg-white text-[#D4AF37] hover:bg-gray-100"
            >
              Alle Produkte ansehen
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SeasonalLandingPage;
